import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, X, ArrowRight, HelpCircle,
  Users, FileText, Star, Mail, BarChart3,
  Bot, Shield, Calendar, MessageSquare,
  Globe, Share2, Layout, Award
} from 'lucide-react';

const Pricing: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');

  const pricingPlans = [
    {
      name: "Growth",
      price: {
        monthly: 297,
        annual: 267
      },
      description: "Perfect for advisors looking to grow their practice with digital marketing.",
      features: [
        "Up to 3 landing pages",
        "Basic CRM",
        "Email campaigns",
        "Social media scheduling",
        "Review management",
        "Task management",
        "Basic analytics",
        "Email support",
        "Standard templates",
        "Google Calendar integration"
      ],
      notIncluded: [
        "Custom domains",
        "Advanced automation",
        "A/B testing",
        "White-label options",
        "API access",
        "Priority support"
      ],
      cta: "Start Free Trial",
      popular: false
    },
    {
      name: "Professional",
      price: {
        monthly: 497,
        annual: 357
      },
      description: "Advanced features for established advisory practices.",
      features: [
        "Up to 10 landing pages",
        "Advanced CRM",
        "Marketing automation",
        "Multi-channel campaigns",
        "Custom domains",
        "A/B testing",
        "Advanced analytics",
        "Priority support",
        "Premium templates",
        "Team collaboration",
        "Client portal",
        "Compliance tools",
        "Custom reporting",
        "Workflow automation"
      ],
      notIncluded: [
        "White-label options",
        "Custom API access",
        "Dedicated success manager"
      ],
      cta: "Start Free Trial",
      popular: true
    },
    {
      name: "Enterprise",
      price: {
        monthly: "Custom",
        annual: "Custom"
      },
      description: "Tailored solutions for large advisory firms and enterprises.",
      features: [
        "Unlimited landing pages",
        "Enterprise CRM",
        "White-label solution",
        "Custom integrations",
        "API access",
        "24/7 priority support",
        "Custom templates",
        "Advanced security",
        "Dedicated success manager",
        "Custom training",
        "Multi-branch support",
        "Enterprise analytics",
        "Custom compliance tools",
        "Advanced automation",
        "SLA guarantees"
      ],
      notIncluded: [],
      cta: "Contact Sales",
      popular: false
    }
  ];

  const featureCategories = [
    {
      name: "Marketing Tools",
      icon: <Layout className="h-5 w-5" />,
      features: [
        {
          name: "Landing Pages",
          growth: "Up to 3",
          professional: "Up to 10",
          enterprise: "Unlimited"
        },
        {
          name: "Custom Domains",
          growth: false,
          professional: true,
          enterprise: true
        },
        {
          name: "A/B Testing",
          growth: false,
          professional: true,
          enterprise: true
        },
        {
          name: "Templates",
          growth: "Standard",
          professional: "Premium",
          enterprise: "Custom"
        }
      ]
    },
    {
      name: "CRM & Client Management",
      icon: <Users className="h-5 w-5" />,
      features: [
        {
          name: "Contact Limit",
          growth: "250",
          professional: "1,000",
          enterprise: "Unlimited"
        },
        {
          name: "Client Portal",
          growth: false,
          professional: true,
          enterprise: true
        },
        {
          name: "Team Collaboration",
          growth: false,
          professional: true,
          enterprise: true
        },
        {
          name: "Workflow Automation",
          growth: "Basic",
          professional: "Advanced",
          enterprise: "Custom"
        }
      ]
    },
    {
      name: "Social Media & Reviews",
      icon: <Share2 className="h-5 w-5" />,
      features: [
        {
          name: "Social Scheduling",
          growth: "Basic",
          professional: "Advanced",
          enterprise: "Enterprise"
        },
        {
          name: "Review Management",
          growth: "Basic",
          professional: "Advanced",
          enterprise: "Custom"
        },
        {
          name: "Multi-Channel Campaigns",
          growth: false,
          professional: true,
          enterprise: true
        },
        {
          name: "Content Library",
          growth: "Limited",
          professional: "Full Access",
          enterprise: "Custom"
        }
      ]
    },
    {
      name: "Analytics & Reporting",
      icon: <BarChart3 className="h-5 w-5" />,
      features: [
        {
          name: "Performance Analytics",
          growth: "Basic",
          professional: "Advanced",
          enterprise: "Enterprise"
        },
        {
          name: "Custom Reports",
          growth: false,
          professional: true,
          enterprise: true
        },
        {
          name: "ROI Tracking",
          growth: "Basic",
          professional: "Advanced",
          enterprise: "Custom"
        },
        {
          name: "Data Export",
          growth: "CSV",
          professional: "All Formats",
          enterprise: "API Access"
        }
      ]
    }
  ];

  const faqs = [
    {
      question: "Do you offer a free trial?",
      answer: "Yes! We offer a 14-day free trial on our Growth and Professional plans. No credit card required to start."
    },
    {
      question: "Can I upgrade or downgrade my plan?",
      answer: "Yes, you can change your plan at any time. When upgrading, you'll be prorated for the remainder of your billing period. When downgrading, changes take effect at the next billing cycle."
    },
    {
      question: "Is there a setup fee?",
      answer: "No, there are no setup fees for our Growth and Professional plans. Enterprise plans may include setup fees depending on customization requirements."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards (Visa, Mastercard, American Express) and ACH bank transfers for annual plans."
    },
    {
      question: "Do you offer refunds?",
      answer: "We offer a 30-day money-back guarantee if you're not satisfied with our service. Enterprise plans have custom refund policies."
    },
    {
      question: "What kind of support is included?",
      answer: "Growth plans include email support with 24-hour response time. Professional plans get priority email support with 4-hour response time. Enterprise plans include 24/7 phone and email support with a dedicated success manager."
    }
  ];

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="section bg-gradient-to-b from-gray-50 to-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-6">
              Choose the Right Plan for Your Practice
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Everything you need to attract, convert, and retain high-value clients. Scale your practice with confidence.
            </p>
            <div className="inline-flex items-center space-x-2 bg-white rounded-lg p-1 border">
              <button 
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  billingCycle === 'monthly' 
                    ? 'bg-primary text-white' 
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setBillingCycle('monthly')}
              >
                Monthly
              </button>
              <button 
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  billingCycle === 'annual' 
                    ? 'bg-primary text-white' 
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setBillingCycle('annual')}
              >
                Annual (Save 10%)
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="section pt-0">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index} 
                className={`relative card flex flex-col ${
                  plan.popular ? 'border-2 border-primary ring-4 ring-primary/10' : 'border border-gray-200'
                }`}
              >
                <div className="p-6 flex-grow">
                  <div className="flex items-center gap-2">
                    <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                    {plan.popular && (
                      <span className="inline-block bg-primary text-white text-sm font-semibold py-1 px-3 rounded-full">
                        Most Popular
                      </span>
                    )}
                  </div>
                  <div className="mt-4 flex items-baseline">
                    {typeof plan.price[billingCycle] === 'number' ? (
                      <>
                        <span className="text-4xl font-bold text-gray-900">
                          ${plan.price[billingCycle]}
                        </span>
                        <span className="text-gray-600 ml-1">/month</span>
                      </>
                    ) : (
                      <span className="text-4xl font-bold text-gray-900">
                        {plan.price[billingCycle]}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-gray-600">{plan.description}</p>
                  
                  <div className="mt-6">
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
                      Included Features:
                    </h4>
                    <ul className="space-y-3">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start">
                          <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {plan.notIncluded.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
                        Not Included:
                      </h4>
                      <ul className="space-y-3">
                        {plan.notIncluded.map((feature, i) => (
                          <li key={i} className="flex items-start">
                            <X className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
                            <span className="text-gray-500">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                
                <div className="p-6 bg-gray-50 border-t border-gray-200">
                  <Link 
                    to={plan.cta === "Contact Sales" ? "/contact" : "/signup"}
                    className={`btn w-full ${
                      plan.popular ? 'btn-primary' : 'btn-outline'
                    }`}
                  >
                    {plan.cta}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="section bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Compare Features</h2>
            <p className="text-lg text-gray-600">
              Detailed comparison of features across all plans
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-4 px-6 text-left text-gray-900 font-semibold">Features</th>
                  <th className="py-4 px-6 text-center text-gray-900 font-semibold">Growth</th>
                  <th className="py-4 px-6 text-center text-gray-900 font-semibold">Professional</th>
                  <th className="py-4 px-6 text-center text-gray-900 font-semibold">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {featureCategories.map((category, categoryIndex) => (
                  <React.Fragment key={categoryIndex}>
                    <tr className="bg-gray-50">
                      <td colSpan={4} className="py-3 px-6">
                        <div className="flex items-center font-semibold text-gray-900">
                          {category.icon}
                          <span className="ml-2">{category.name}</span>
                        </div>
                      </td>
                    </tr>
                    {category.features.map((feature, featureIndex) => (
                      <tr key={featureIndex} className="border-t border-gray-200">
                        <td className="py-3 px-6 text-gray-900">{feature.name}</td>
                        <td className="py-3 px-6 text-center">
                          {typeof feature.growth === 'boolean' ? (
                            feature.growth ? (
                              <Check className="h-5 w-5 text-green-500 mx-auto" />
                            ) : (
                              <X className="h-5 w-5 text-gray-400 mx-auto" />
                            )
                          ) : (
                            <span className="text-gray-700">{feature.growth}</span>
                          )}
                        </td>
                        <td className="py-3 px-6 text-center">
                          {typeof feature.professional === 'boolean' ? (
                            feature.professional ? (
                              <Check className="h-5 w-5 text-green-500 mx-auto" />
                            ) : (
                              <X className="h-5 w-5 text-gray-400 mx-auto" />
                            )
                          ) : (
                            <span className="text-gray-700">{feature.professional}</span>
                          )}
                        </td>
                        <td className="py-3 px-6 text-center">
                          {typeof feature.enterprise === 'boolean' ? (
                            feature.enterprise ? (
                              <Check className="h-5 w-5 text-green-500 mx-auto" />
                            ) : (
                              <X className="h-5 w-5 text-gray-400 mx-auto" />
                            )
                          ) : (
                            <span className="text-gray-700">{feature.enterprise}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white shadow-sm rounded-lg overflow-hidden">
                  <details className="group">
                    <summary className="flex justify-between items-center font-medium cursor-pointer p-4 text-gray-900">
                      <span>{faq.question}</span>
                      <span className="transition group-open:rotate-180">
                        <HelpCircle className="h-5 w-5 text-gray-400" />
                      </span>
                    </summary>
                    <p className="p-4 pt-0 text-gray-600">{faq.answer}</p>
                  </details>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-gray-50">
        <div className="container-custom">
          <div className="bg-primary rounded-2xl overflow-hidden shadow-xl">
            <div className="px-6 py-12 sm:px-12 sm:py-16 lg:flex lg:items-center lg:justify-between">
              <div>
                <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  Ready to grow your practice?
                </h2>
                <p className="mt-4 text-lg text-white/90 max-w-2xl">
                  Start your 14-day free trial today. No credit card required.
                </p>
              </div>
              <div className="mt-8 lg:mt-0 lg:flex-shrink-0">
                <Link
                  to="/signup"
                  className="btn bg-white text-primary hover:bg-gray-100"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;